import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import CollapsiblePanel from "./CollapsiblePanel.js";

// Mock data structure based on your buildTransactionGraphData output
const mockGraphData = {
  nodes: [
    { id: 'account:Checking', type: 'account', label: 'Checking Account', amount: 5420.50, transactionCount: 45 },
    { id: 'account:Savings', type: 'account', label: 'Savings Account', amount: 1200.00, transactionCount: 8 },
    { id: 'category:Food', type: 'category', label: 'Food & Dining', amount: 2100.30, transactionCount: 28 },
    { id: 'category:Shopping', type: 'category', label: 'Shopping', amount: 1800.75, transactionCount: 15 },
    { id: 'category:Transportation', type: 'category', label: 'Transportation', amount: 890.25, transactionCount: 12 },
    { id: 'subcategory:Groceries', type: 'subcategory', label: 'Groceries', amount: 1200.50, transactionCount: 18 },
    { id: 'subcategory:Restaurants', type: 'subcategory', label: 'Restaurants', amount: 899.80, transactionCount: 10 },
    { id: 'subcategory:Clothing', type: 'subcategory', label: 'Clothing', amount: 600.25, transactionCount: 6 },
    { id: 'subcategory:Gas', type: 'subcategory', label: 'Gas Stations', amount: 450.00, transactionCount: 8 },
    { id: 'merchant:Walmart', type: 'merchant', label: 'Walmart', amount: 850.25, transactionCount: 12 },
    { id: 'merchant:McDonalds', type: 'merchant', label: "McDonald's", amount: 245.75, transactionCount: 8 },
    { id: 'merchant:Amazon', type: 'merchant', label: 'Amazon', amount: 1200.50, transactionCount: 9 },
    { id: 'merchant:Shell', type: 'merchant', label: 'Shell Gas', amount: 340.00, transactionCount: 6 }
  ],
  edges: [
    { id: 'account:Checking-category:Food', source: 'account:Checking', target: 'category:Food', type: 'spent_on', amount: 1800.30, transactionCount: 25 },
    { id: 'account:Checking-category:Shopping', source: 'account:Checking', target: 'category:Shopping', type: 'spent_on', amount: 1500.75, transactionCount: 12 },
    { id: 'account:Checking-category:Transportation', source: 'account:Checking', target: 'category:Transportation', type: 'spent_on', amount: 890.25, transactionCount: 12 },
    { id: 'category:Food-subcategory:Groceries', source: 'category:Food', target: 'subcategory:Groceries', type: 'subcategorized_as', amount: 1200.50, transactionCount: 18 },
    { id: 'category:Food-subcategory:Restaurants', source: 'category:Food', target: 'subcategory:Restaurants', type: 'subcategorized_as', amount: 899.80, transactionCount: 10 },
    { id: 'category:Shopping-subcategory:Clothing', source: 'category:Shopping', target: 'subcategory:Clothing', type: 'subcategorized_as', amount: 600.25, transactionCount: 6 },
    { id: 'subcategory:Groceries-merchant:Walmart', source: 'subcategory:Groceries', target: 'merchant:Walmart', type: 'subcategorized_as', amount: 850.25, transactionCount: 12 },
    { id: 'subcategory:Restaurants-merchant:McDonalds', source: 'subcategory:Restaurants', target: 'merchant:McDonalds', type: 'subcategorized_as', amount: 245.75, transactionCount: 8 },
    { id: 'subcategory:Clothing-merchant:Amazon', source: 'subcategory:Clothing', target: 'merchant:Amazon', type: 'subcategorized_as', amount: 600.50, transactionCount: 4 },
    { id: 'subcategory:Gas-merchant:Shell', source: 'subcategory:Gas', target: 'merchant:Shell', type: 'subcategorized_as', amount: 340.00, transactionCount: 6 }
  ]
};

// Define different perspective configurations
const PERSPECTIVES = {
  'account-category-subcategory-merchant': {
    name: 'Account → Category → Subcategory → Merchant',
    hierarchy: ['account', 'category', 'subcategory', 'merchant'],
    description: 'Full view showing detailed subcategory flow to merchants'
  },
  'account-category-merchant': {
    name: 'Account → Category → Merchant',
    hierarchy: ['account', 'category', 'merchant'],
    description: 'High level view showing category flow to merchants'
  },
  'account-merchant': {
    name: 'Account → Merchant',
    hierarchy: ['account', 'merchant'],
    description: 'High-level spending patterns by merchant'
  },
  // 'category-subcategory-merchant': {
  //   name: 'Category → Subcategory → Merchant',
  //   hierarchy: ['category', 'subcategory', 'merchant'],
  //   description: 'Category breakdown without account context'
  // },
  'account-category': {
    name: 'Account → Category',
    hierarchy: ['account', 'category'],
    description: 'High-level spending patterns by category'
  },
  'account-category-subcategory': {
    name: 'Account → Category → Subcategory',
    hierarchy: ['account', 'category', 'subcategory'],
    description: 'Detailed spending patterns by subcategory'
  }
};

// Filter edges based on perspective
const filterEdgesForPerspective = (edges, nodes, perspective) => {
  const hierarchy = PERSPECTIVES[perspective].hierarchy;

  return edges.filter(edge => {
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);

    if (!sourceNode || !targetNode) return false;
    for (let i = 0; i < hierarchy.length - 1; i++) {
      const parent = hierarchy[i];
      const child = hierarchy[i + 1];

      console.log(`Parent: ${parent}, Child: ${child}`);
      // Your comparison logic here
      if (sourceNode.type === parent && targetNode.type === child) {
        return true;
      }
      if (sourceNode.type === child && targetNode.type === parent) {
        return true;
      }
    }
    // Include edge if both source and target types are in our hierarchy
    // return hierarchy.includes(sourceNode.type) && hierarchy.includes(targetNode.type);
  });
};

// Helper function to detect reverse flow edges
const isReverseFlow = (edge, nodes) => {
  const sourceNode = nodes.find(n => n.id === edge.source);
  const targetNode = nodes.find(n => n.id === edge.target);

  if (!sourceNode || !targetNode) return false;

  // Define hierarchy order
  const hierarchyOrder = {
    'merchant': 0,
    'subcategory': 1,
    'category': 2,
    'account': 3
  };

  const sourceLevel = hierarchyOrder[sourceNode.type] ?? 999;
  const targetLevel = hierarchyOrder[targetNode.type] ?? 999;

  // Reverse flow is when going from lower level to higher level (e.g., merchant to account)
  return sourceLevel < targetLevel;
};

// Amount gradient color function - dark red to bright red for outbound, dark green to bright green for inbound
const getAmountGradientColor = (amount, minAmount, maxAmount, isReverse = false) => {
  if (maxAmount === minAmount) {
    return isReverse ? '#4CAF50' : '#cc0000';
  }

  const ratio = (amount - minAmount) / (maxAmount - minAmount);

  if (isReverse) {
    // Dark green to bright green gradient for inbound flows
    const red = 0;
    const green = Math.floor(100 + (ratio * 155)); // 100 to 255 (dark green to bright green)
    const blue = 0;
    return `rgb(${red}, ${green}, ${blue})`;
  } else {
    // Dark red to bright red gradient for outbound flows
    const red = Math.floor(100 + (ratio * 155)); // 100 to 255 (dark red to bright red)
    const green = 0;
    const blue = 0;
    return `rgb(${red}, ${green}, ${blue})`;
  }
};

// Cytoscape Network Visualization Component
const CytoscapeNetworkVisualization = ({ nodes, edges, perspective }) => {
  const cyRef = useRef(null);
  const cyInstanceRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Convert data to Cytoscape format
  const cytoscapeData = useMemo(() => {
    const elements = [];

    // Calculate min/max amounts for scaling
    const nodeAmounts = nodes.map(n => Math.abs(n.amount));
    const edgeAmounts = edges.map(e => Math.abs(e.amount));

    const minNodeAmount = Math.min(...nodeAmounts);
    const maxNodeAmount = Math.max(...nodeAmounts);
    const minEdgeAmount = Math.min(...edgeAmounts);
    const maxEdgeAmount = Math.max(...edgeAmounts);

    // Add nodes with scaled sizes
    nodes.forEach(node => {
      const colors = {
        account: '#2196F3',
        category: '#4CAF50',
        subcategory: '#FF9800',
        merchant: '#9C27B0'
      };

      // Scale node size based on amount (50px to 150px width, 30px to 90px height)
      const sizeScale = nodeAmounts.length > 1
        ? (Math.abs(node.amount) - minNodeAmount) / (maxNodeAmount - minNodeAmount)
        : 0.5;
      const nodeWidth = 50 + (sizeScale * 100); // 50px to 150px
      const nodeHeight = 30 + (sizeScale * 60);  // 30px to 90px

      elements.push({
        data: {
          id: node.id,
          label: node.label,
          type: node.type,
          amount: node.amount,
          transactionCount: node.transactionCount,
          formattedAmount: formatAmount(node.amount),
          color: colors[node.type] || '#757575',
          width: nodeWidth,
          height: nodeHeight
        }
      });
    });

    // Add edges with amount gradient colors
    edges.forEach(edge => {
      // Scale edge width based on amount (1px to 8px)
      const thicknessScale = edgeAmounts.length > 1
        ? (Math.abs(edge.amount) - minEdgeAmount) / (maxEdgeAmount - minEdgeAmount)
        : 0.5;
      const edgeWidth = 1 + (thicknessScale * 7); // 1px to 8px

      // Check if this is a reverse flow edge
      const reverse = isReverseFlow(edge, nodes);

      // Get gradient color based on amount and flow direction
      const edgeColor = getAmountGradientColor(edge.amount, minEdgeAmount, maxEdgeAmount, reverse);

      elements.push({
        data: {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          type: edge.type,
          amount: edge.amount,
          transactionCount: edge.transactionCount,
          formattedAmount: formatAmount(edge.amount),
          width: edgeWidth,
          color: edgeColor,
          isReverse: reverse
        }
      });
    });

    return elements;
  }, [nodes, edges]);

  // Cytoscape styles
  const cytoscapeStyle = [
    {
      selector: 'node',
      style: {
        'background-color': 'data(color)',
        'label': 'data(label)',
        'width': 'data(width)', // Dynamic width based on amount
        'height': 'data(height)', // Dynamic height based on amount
        'shape': 'round-rectangle',
        'text-valign': 'center',
        'text-halign': 'center',
        'font-size': function (ele) {
          // Scale font size with node size (8px to 12px)
          const width = ele.data('width');
          return Math.max(8, Math.min(12, width / 10));
        },
        'font-weight': 'bold',
        'color': 'white',
        'text-wrap': 'wrap',
        'text-max-width': function (ele) {
          return ele.data('width') - 10; // Leave 10px padding
        },
        'border-width': 2,
        'border-color': '#333',
        'text-outline-width': 1,
        'text-outline-color': 'data(color)',
        'overlay-padding': 6,
        'z-index': 10
      }
    },
    {
      selector: 'node:selected',
      style: {
        'border-width': 4,
        'border-color': '#000',
        'overlay-opacity': 0.2,
        'overlay-color': '#000'
      }
    },
    {
      selector: 'edge',
      style: {
        'width': 'data(width)', // Dynamic width based on amount
        'line-color': 'data(color)', // Amount-based gradient color
        'target-arrow-color': 'data(color)', // Matching arrow color
        'target-arrow-shape': 'triangle',
        'curve-style': 'bezier',
        'arrow-scale': function (ele) {
          // Scale arrow with edge thickness
          const width = ele.data('width');
          return 0.8 + (width * 0.15);
        },
        'control-point-step-size': 40,
        'label': 'data(formattedAmount)',
        'font-size': function (ele) {
          // Scale font size with edge thickness (8px to 11px)
          const width = ele.data('width');
          return Math.max(8, Math.min(11, 7 + width));
        },
        'text-rotation': 'autorotate',
        'text-margin-y': -10,
        'color': '#333',
        'text-background-color': 'white',
        'text-background-opacity': 0.9,
        'text-background-padding': 2,
        'text-border-width': 1,
        'text-border-color': '#ccc'
      }
    },
    {
      selector: 'edge:selected',
      style: {
        'line-color': '#333',
        'target-arrow-color': '#333'
      }
    }
  ];

  // Initialize Cytoscape
  useEffect(() => {
    // Use dynamic import for Cytoscape since it's installed via npm
    const initializeCytoscape = async () => {
      // Ensure the container is ready
      if (!cyRef.current) {
        console.log('Container not ready yet');
        return;
      }

      try {
        // Import Cytoscape
        const cytoscape = (await import('cytoscape')).default;

        // Try to import dagre layout, but fallback if it fails
        let layoutConfig = {
          name: 'grid',
          rows: 3,
          cols: 4,
          padding: 30
        };

        try {
          const dagre = (await import('cytoscape-dagre')).default;
          cytoscape.use(dagre);
          layoutConfig = {
            name: 'dagre',
            directed: true,
            padding: 20,
            spacingFactor: 1.5,
            nodeDimensionsIncludeLabels: true,
            rankSep: 100,
            nodeSep: 80
          };
          console.log('Using Dagre layout');
        } catch (dagreError) {
          console.log('Dagre not available, using grid layout:', dagreError);
        }

        const cy = cytoscape({
          container: cyRef.current,
          elements: cytoscapeData,
          style: cytoscapeStyle,
          layout: layoutConfig,
          zoomingEnabled: true,
          userZoomingEnabled: true,
          panningEnabled: true,
          userPanningEnabled: true,
          wheelSensitivity: 0.5, // Increased from 0.2 for faster zoom
          minZoom: 0.1,
          maxZoom: 4,
          // Add renderer options to prevent the error
          renderer: {
            name: 'canvas'
          }
        });

        // Wait for layout to complete before adding event listeners
        cy.ready(() => {
          console.log('Cytoscape ready');

          // Add event listeners
          cy.on('tap', 'node', (evt) => {
            const node = evt.target;
            const nodeId = node.id();
            setSelectedNode(selectedNode === nodeId ? null : nodeId);
          });

          // Add tooltips
          cy.on('mouseover', 'node', (evt) => {
            const node = evt.target;
            const data = node.data();

            // Remove existing tooltip
            const existingTooltip = document.getElementById('cy-tooltip');
            if (existingTooltip) {
              existingTooltip.remove();
            }

            // Create tooltip content
            const tooltip = document.createElement('div');
            tooltip.id = 'cy-tooltip';
            tooltip.style.cssText = `
              position: fixed;
              background: #333;
              color: white;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 11px;
              pointer-events: none;
              z-index: 1000;
              white-space: nowrap;
              box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            `;
            tooltip.innerHTML = `
              <div style="font-weight: bold; margin-bottom: 4px;">${data.label}</div>
              <div style="margin-bottom: 2px; text-transform: uppercase; font-size: 10px; opacity: 0.8;">${data.type}</div>
              <div style="margin-bottom: 2px;">Transactions: ${data.transactionCount}</div>
              <div>Total: ${data.formattedAmount}</div>
            `;

            document.body.appendChild(tooltip);

            // Position tooltip
            const updateTooltipPosition = (event) => {
              const x = event.originalEvent ? event.originalEvent.pageX : event.renderedPosition.x;
              const y = event.originalEvent ? event.originalEvent.pageY : event.renderedPosition.y;
              tooltip.style.left = (x - tooltip.offsetWidth / 2) + 'px';
              tooltip.style.top = (y - tooltip.offsetHeight - 10) + 'px';
            };

            updateTooltipPosition(evt);
          });

          cy.on('mouseout', 'node', () => {
            const tooltip = document.getElementById('cy-tooltip');
            if (tooltip) {
              tooltip.remove();
            }
          });

          // Add edge tooltips to show flow direction
          cy.on('mouseover', 'edge', (evt) => {
            const edge = evt.target;
            const data = edge.data();

            // Remove existing tooltip
            const existingTooltip = document.getElementById('cy-tooltip');
            if (existingTooltip) {
              existingTooltip.remove();
            }

            // Create tooltip content
            const tooltip = document.createElement('div');
            tooltip.id = 'cy-tooltip';
            tooltip.style.cssText = `
              position: fixed;
              background: #333;
              color: white;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 11px;
              pointer-events: none;
              z-index: 1000;
              white-space: nowrap;
              box-shadow: 0 4px 12px rgba(0,0,0,0.3);
              border-left: 4px solid ${data.color};
            `;
            tooltip.innerHTML = `
              <div style="font-weight: bold; margin-bottom: 4px;">${data.formattedAmount}</div>
              <div style="margin-bottom: 2px;">Transactions: ${data.transactionCount}</div>
              <div style="font-size: 10px; opacity: 0.8;">${data.isReverse ? 'Inbound Flow' : 'Outbound Flow'}</div>
            `;

            document.body.appendChild(tooltip);

            // Position tooltip
            const updateTooltipPosition = (event) => {
              const x = event.originalEvent ? event.originalEvent.pageX : event.renderedPosition.x;
              const y = event.originalEvent ? event.originalEvent.pageY : event.renderedPosition.y;
              tooltip.style.left = (x - tooltip.offsetWidth / 2) + 'px';
              tooltip.style.top = (y - tooltip.offsetHeight - 10) + 'px';
            };

            updateTooltipPosition(evt);
          });

          cy.on('mouseout', 'edge', () => {
            const tooltip = document.getElementById('cy-tooltip');
            if (tooltip) {
              tooltip.remove();
            }
          });
        });

        cyInstanceRef.current = cy;

      } catch (error) {
        console.error('Error loading Cytoscape:', error);
        // Fallback message
        if (cyRef.current) {
          cyRef.current.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666; flex-direction: column;">
              <div style="font-size: 18px; margin-bottom: 10px;">Cytoscape.js Error</div>
              <div style="font-size: 14px; margin-bottom: 10px;">Please install: npm install cytoscape cytoscape-dagre</div>
              <div style="font-size: 12px; color: #999;">${error.message}</div>
            </div>
          `;
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initializeCytoscape();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (cyInstanceRef.current) {
        cyInstanceRef.current.destroy();
        cyInstanceRef.current = null;
      }
      const tooltip = document.getElementById('cy-tooltip');
      if (tooltip) {
        tooltip.remove();
      }
    };
  }, [cytoscapeData]);

  // Update layout when data changes
  useEffect(() => {
    if (cyInstanceRef.current) {
      cyInstanceRef.current.elements().remove();
      cyInstanceRef.current.add(cytoscapeData);

      // Re-run layout
      cyInstanceRef.current.layout({
        name: 'dagre',
        directed: true,
        padding: 20,
        spacingFactor: 1.5,
        nodeDimensionsIncludeLabels: true,
        rankSep: 100,
        nodeSep: 80
      }).run();
    }
  }, [cytoscapeData]);

  return (
    <div style={{ position: 'relative' }}>
      {/* Cytoscape container */}
      <div
        ref={cyRef}
        style={{
          width: '100%',
          height: '600px',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#fafafa'
        }}
      />

      {/* Instructions overlay */}
      {/* <div style={{
        position: 'absolute',
        bottom: '10px',
        left: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '4px',
        fontSize: '11px',
        zIndex: 1000
      }}>
        <div>Mouse wheel: Zoom • Drag: Pan • Click: Select</div>
        <div>Hover nodes/edges for details • Amount-based gradient colors</div>
        <div style={{ marginTop: '4px', fontSize: '10px' }}>
          <span style={{ color: '#cc0000' }}>■</span> Outbound Flow (Dark Red→Bright Red) •
          <span style={{ color: '#006400' }}> ■</span> Inbound Flow (Dark Green→Bright Green)
        </div>
      </div> */}
    </div>
  );
};

const TransactionNetworkVisualization = (props) => {
  const defaultProps = { graphData: mockGraphData };
  props = { ...defaultProps, ...props };
  const { graphData } = props;

  const [selectedPerspective, setSelectedPerspective] = useState('account-category-subcategory-merchant');

  const handlePerspectiveChange = useCallback((event) => {
    setSelectedPerspective(event.target.value);
  }, []);

  const currentPerspective = PERSPECTIVES[selectedPerspective];

  const filteredNodes = graphData.nodes.filter(node =>
    currentPerspective.hierarchy.includes(node.type)
  );

  const filteredEdges = filterEdgesForPerspective(
    graphData.edges,
    graphData.nodes,
    selectedPerspective
  );
  const accountNodes = filteredNodes.filter(node => node.type === 'account');
  const totalAmount = accountNodes.reduce((sum, n) => sum + n.amount, 0);
  const totalTransactions = accountNodes.reduce((sum, n) => sum + n.transactionCount, 0);

  // Count normal vs reverse flow edges
  const reverseFlowCount = filteredEdges.filter(edge => isReverseFlow(edge, graphData.nodes)).length;
  const normalFlowCount = filteredEdges.length - reverseFlowCount;

  return (
    <div style={{
      padding: '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
      {/* <h1 style={{
        fontSize: '28px',
        fontWeight: 'bold',
        marginBottom: '24px',
        color: '#333'
      }}>
        Transaction Network Analysis with Amount Gradients
      </h1> */}

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginBottom: '20px'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Analysis Perspective
          </label>
          <select
            value={selectedPerspective}
            onChange={handlePerspectiveChange}
            style={{
              padding: '12px',
              borderRadius: '8px',
              border: '2px solid #e0e0e0',
              fontSize: '14px',
              backgroundColor: 'white',
              width: '100%'
            }}
          >
            {Object.entries(PERSPECTIVES).map(([key, perspective]) => (
              <option key={key} value={key}>
                {perspective.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{
          padding: '16px',
          backgroundColor: 'white',
          border: '2px solid #e0e0e0',
          borderRadius: '8px'
        }}>
          <h3 style={{ margin: '0 0 8px 0', color: '#2196F3', fontSize: '16px' }}>
            Analysis Perspective Details
          </h3>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            {currentPerspective.description}
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            <div style={{ marginBottom: '2px' }}>
              <span style={{ color: '#cc0000', fontWeight: 'bold' }}>■</span> Outbound: Dark Red → Bright Red based on spend amount
            </div>
            <div>
              <span style={{ color: '#006400', fontWeight: 'bold' }}>■</span> Inbound: Dark Green → Bright Green based on income amount
            </div>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: '16px',
        marginBottom: '20px'
      }}>
        <div style={{
          padding: '20px',
          backgroundColor: 'white',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196F3', marginBottom: '4px' }}>
            {filteredNodes.length}
          </div>
          <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>
            Nodes
          </div>
        </div>
        <div style={{
          padding: '20px',
          backgroundColor: 'white',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#9C27B0', marginBottom: '4px' }}>
            {filteredEdges.length}
          </div>
          <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>
            Total Edges
          </div>
        </div>
        <div style={{
          padding: '20px',
          backgroundColor: 'white',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#cc0000', marginBottom: '4px' }}>
            {normalFlowCount}
          </div>
          <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>
            Outbound Flow
          </div>
        </div>
        <div style={{
          padding: '20px',
          backgroundColor: 'white',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#006400', marginBottom: '4px' }}>
            {reverseFlowCount}
          </div>
          <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>
            Inbound Flow
          </div>
        </div>
        <div style={{
          padding: '20px',
          backgroundColor: 'white',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4CAF50', marginBottom: '4px' }}>
            ${totalAmount.toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>
            Total Amount
          </div>
        </div>
        <div style={{
          padding: '20px',
          backgroundColor: 'white',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FF9800', marginBottom: '4px' }}>
            {totalTransactions}
          </div>
          <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>
            Transactions
          </div>
        </div>
      </div>

      {/* Network Visualization */}
      <div style={{
        padding: '0',
        backgroundColor: 'white',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '2px solid #e0e0e0'
      }}>
        <CytoscapeNetworkVisualization
          nodes={filteredNodes}
          edges={filteredEdges}
          perspective={selectedPerspective}
        />
      </div>

      {/* Usage Instructions */}
      <CollapsiblePanel title="Usage Instructions" defaultCollapsed={true}>
      {/* <div style={{
        padding: '16px',
        backgroundColor: 'white',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        marginTop: '20px'
      }}> */}
        {/* <h3 style={{ margin: '0 0 12px 0', color: '#333' }}>Amount Gradient Features</h3> */}
        <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
          <li><strong>Interactive Tooltips:</strong> Hover edges to see flow direction and amounts</li>
          <li><strong>Navigation:</strong> Mouse wheel zoom, drag to pan, click nodes to select</li>
        </ul>
      {/* </div> */}
      </CollapsiblePanel>
    </div>
  );
};

export default TransactionNetworkVisualization;